import axios from "axios";
import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import AccessBar from "@/components/AccessBar";
import LeadFormMasterclass from "@/components/LeadFormMasterclass";
import AboutInstructor from "@/components/AboutInstructor";
import ContactUs from "@/components/ContactUs";
import { useRouter } from "next/router";
import QuestionAnswerTile from "@/components/QuestionAnswerTile";
import Image from "next/image";
import webengage from "@/webengage";
import { formatNumber } from "@/components/bootcamp/BootcampTabs";
import mixpanel from "@/utils/mixpanel";
import Head from "next/head";
import Breadcrumb from "@/components/BreadCrumb";

type SpecialPageKeys = keyof typeof SPECIAL_PAGES;

type SpecialPage = {
  title: string;
  desc: string;
};

const SPECIAL_PAGES: Record<string, SpecialPage> = {
  "vedic-numerology-by-ameeta-nhectyx7u": {
    title: "Vedic Numerology Workshop by Ameeta | Astrolearn",
    desc: "Join Ameeta's Vedic Numerology masterclass and explore the power of numbers. Learn ancient techniques to decode life patterns and enhance your skills.",
  },
  "master-numerology-with-vivek-vats--cd23fs418": {
    title:
      "Numerology Workshop | Master Numerology with Vivek Vats | Astrolearn",
    desc: "Join the Numerology Workshop by Vivek Vats and master the art of numerology. Gain deeper insights into your destiny with expert guidance at AstroLearn.",
  },
  "bhrigu-nandi-nadi-astrology-with-vaibhav-4bp3m8tqk": {
    title: "Bhrigu Nandi Nadi Course | By Vaibhav Gupta | Astrolearn",
    desc: "Unlock the secrets of Bhrigu Nandi Nadi astrology in this exclusive bootcamp with Vaibhav. Learn ancient techniques to predict life events and gain insights into your destiny",
  },
  "master-lal-kitab-with-mahesh-juhcr80ij": {
    title: "Lal Kitab Astrology | Master Lal Kitab with Mahesh | AstroLearn",
    desc: "Master Lal Kitab astrology with Mahesh. Learn powerful techniques to decode planetary influences and enhance life predictions in this expert-led class.",
  },
  "pure-crystals--discovering-the-mysteries-of-most-precious-stones-3f4ue28bt":
    {
      title:
        "Crystal Healing | Discovering the Mysteries of Most Precious Stones",
      desc: "Join the Pure Crystals masterclass to explore the power of precious stones. Unveil their secrets, healing properties, and spiritual benefits.",
    },
  "mystical-triangle-with-dr-rohet-sethi-3q4qgdvwl": {
    title: "Master Mystical Triangle with Dr. Rohet Sethi | AstroLearn",
    desc: "Unlock the secrets of the Mystical Triangle with Dr. Rohet Sethi. Explore ancient knowledge and spiritual insights through this expert-led masterclass.",
  },
  "ultimate-vastu-mastery-vjlh16ftv": {
    title: "Vastu Course Online: Ultimate Vastu Mastery by Chaitanyaa Missra",
    desc: "Master Vastu Shastra with Chaitanyaa Missra's ultimate online course. Learn techniques to harmonize spaces and attract positive energy.",
  },
  "make-money-using-astrology-yusni580z": {
    title: "Financial Astrology Masterclass - By Shivam Aggarwal | Astrolearn",
    desc: "Unlock the secrets of making money through astrology. Join our expert-led masterclass and learn how to leverage astrology for financial success and career growth.",
  },
  "discover-the-power-of-ramal-shastra-with-aarti-ahuja-s50y39gvp": {
    title: "Learn Ramal Astrology With Aarti Ahuja | AstroLearn",
    desc: "Unlock the ancient secrets of Ramal Shastra with Aarti Ahuja. Learn powerful techniques to predict and analyze life events in this expert-led masterclass.",
  },
  "masterclass-on-bnn-by-saurabh-kapoor-0ln85w8v9": {
    title: "Bhrigu Nandi Nadi Course | By Saurabh Kapoor | Astrolearn",
    desc: "Unlock the secrets of Bhrigu Nandi Nadi astrology in this exclusive bootcamp. Learn ancient techniques to predict life events & gain insights into your destiny.",
  },
  "beyond-this-lifetime-connecting-with-your-past-lives-2wi89nvqb": {
    title: "Past Life Regression Course - By Dr. Tarun Malik",
    desc: "Explore your past lives in the 'Healing Through History' masterclass. Gain profound insights, heal karmic patterns, and connect with your soul's journey.",
  },
  "swar-vigyan-with-rohet-dff1mryig": {
    title: "Swar Vigyan with Dr. Rohet Sethi | Unlock the Science of Breath",
    desc: "Master the ancient knowledge of Swar Vigyan with Rohet Sethi. Learn how the science of breath impacts life and destiny in this transformative masterclass.",
  },
  "wellness-by-remedies-by-acharya-pravin--5e2h73ekz": {
    title: "Wellness by Remedies with Acharya Pravin | Heal and Transform",
    desc: "Discover holistic wellness with Acharya Pravin. Learn powerful remedies to balance energies, enhance well-being, and transform your life in this masterclass.",
  },
  "masterclass-on-crystal-healing-and-pendulum-dowsing-pdlkprd0p": {
    title: "Masterclass on Crystal Healing With Dr. Rohet Sethi | AstroLearn",
    desc: "Explore the power of crystals and pendulum dowsing in this expert-led masterclass. Learn energy healing techniques to balance your life and amplify your well-being.",
  },
  "mastering-reiki-healing-by-neha-poori-7olhjtuf5": {
    title: "Master Reiki Healing with Neha Poori | AstroLearn",
    desc: "Learn the art of Reiki healing with Neha Poori. Master powerful energy techniques to heal yourself and others in this expert-led transformative masterclass.",
  },
  "kp-astrology-with-prof-krish-murali-eswar-tkdf2ne0i": {
    title: "Master KP Astrology with Prof. Krish Murali Eswar | AstroLearn",
    desc: "Learn KP Astrology with Prof. Krish Murali Eswar. Master precise astrological techniques for accurate predictions in this comprehensive expert-led masterclass.",
  },
  "become-a-professional-tarot-card-consultant-with-jasmine-y7lhbz6dv": {
    title:
      "Tarot Reading Masterclass | Become a Professional Tarot Card Consultant",
    desc: "Master tarot reading with Jasmine's expert guidance. Learn to provide accurate readings and start your journey as a professional tarot consultant today.",
  },
  "aatmanirbhar-jyotish-abhiyan-26p8fnxns": {
    title:
      "Tarot Reading Masterclass | Become a Professional Tarot Card Consultant",
    desc: "Master tarot reading with Jasmine's expert guidance. Learn to provide accurate readings and start your journey as a professional tarot consultant today.",
  },
};

type AccessElement = {
  name: string;
  slug: string;
  iconImage: string;
  iconImageWhenSelected?: string;
};

const OverViewCardRawData = [
  {
    headerText: "Live Session",
    headerImagePath: "/main-website/camera.svg",
    mainBodyText:
      "We provide live classes to our students. You can attend the classes from anywhere in the world",
  },
  {
    headerText: "Gamified Approach",
    headerImagePath: "/main-website/gamified.svg",
    mainBodyText:
      "Enhance your learning with interactive quizzes, challenges, and rewards.Dive into a fun and engaging learning experience!",
  },
  {
    headerText: "Win Prizes upto ₹10,000",
    headerImagePath: "/main-website/prize.svg",
    mainBodyText:
      "We have a communities of 1000+ students who are learning together.",
  },
];

const qNAs = [
  {
    que: "After attending bootcamp, will i be able to take it as my profession?",
    ans: "Yes, we provide sufficient knowledge for an individual to become a professional, but do not forget to practice till the very end 😊",
  },
  {
    que: "Are the bootcamps certified?",
    ans: "Yes, You will be receiving an ISO certified certificate once you complete the boot camp.",
  },
  {
    que: "Will we be getting recordings of the bootcamp sessions?",
    ans: "Yes  and you will be having a life time access to all the recordings",
  },
];
const accessBarItems: AccessElement[] = [
  {
    name: "Overview",
    slug: "overview",
    iconImage: "",
  },
  {
    name: "Instructor",
    slug: "instructor",
    iconImage: "",
  },
  {
    name: "Help",
    slug: "help",
    iconImage: "",
  },
];
export default function Masterclass({ masterclass }: { masterclass: any }) {
  const [accessBarElement, setAccessBarElement] = useState<string>("overview");
  const [starRating, setStarRating] = useState<number>(0);
  const [reviews, setReviews] = useState<number>(0);
  const [years, setYears] = useState<number>(0);
  const [students, setStudents] = useState<number>(0);
  const router = useRouter();
  useEffect(() => {
    if (router?.query?.isEnroll) {
      //@ts-ignore
      document.getElementById("form").scrollIntoView();
    }
  }, []);

  useEffect(() => {
    if (masterclass?.bootcamp.teacher?.achievements) {
      const achievements = masterclass?.bootcamp?.teacher?.achievements.split(";");
      setStarRating(achievements[0] ?? 0);
      setReviews(achievements[1] ?? 0);
      setYears(achievements[4] ?? 0);
      setStudents(achievements[2] ?? 0);
    }
  }, [masterclass?.bootcamp?.teacher?.achievements]);

  const masterclassSlug = router.query.slug;
  const activeSlot = masterclass?.slots.find(
    (slot: any) => slot?.active === true
  );
  const [selectedQna, setSelectedQna] = useState(-1);
  useEffect(() => {
    const _webengage = webengage();
    if (_webengage && masterclass) {
      _webengage.track("Masterclass viewed", {
        title: masterclass.title,
        industry: masterclass?.bootcamp?.teacher?.category?.name,
        fees: "free",
        instructor: masterclass?.bootcamp?.teacher?.name,
        date: activeSlot?.startDateTime && new Date(activeSlot?.startDateTime),
        source: "Astrolearn",
      });
    }
  }, [masterclass]);

  return (
    <>
      <Head>
        <title>
          {SPECIAL_PAGES[masterclass?.slug as SpecialPageKeys]?.title ||
            "Masterclass"}
        </title>
        <meta
          name="description"
          content={
            SPECIAL_PAGES[masterclass?.slug as SpecialPageKeys]?.desc ||
            `Join the ${masterclass?.title} masterclass by ${
              masterclass?.bootcamp?.teacher?.name
            }. Enhance your skills and knowledge in ${
              masterclass?.bootcamp?.teacher?.category?.name || "spirituality"
            }.`
          }
        />
      </Head>
      <Layout childClassName="bg-[#EEEBE6]">
        <div className="flex flex-col justify-center lg:flex-row gap-8 pt-4 px-2 mx-2 lg:relative">
          <div className="md:w-[90%] flex flex-col md:flex-row md:space-x-[31px]">
            <div className="flex flex-col w-full md:w-2/3 md:px-[25px] md:pb-[50px]">
              {/* --- Mobile View --- */}
              <div className="flex gap-4 md:hidden">
                <ArrowLeftIcon
                  className="w-6 h-6 mt-0.5"
                  onClick={() => router.back()}
                />
                <span className="text-[18px] text-black font-lato font-[600] w-full text-left inline">
                  {masterclass?.title}
                </span>
              </div>
              <section className="flex flex-col items-center justify-center text-[#1B1B1B] md:hidden">
                <Image
                  width={872}
                  height={491}
                  src={masterclass?.bootcamp?.coverImage || ""}
                  alt="Bootcamp cover image"
                  className="rounded-md mt-4"
                  loading="eager"
                />
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex flex-row border-b-[1px] mt-[16px] items-center border-dashed border-[#ACACAC] border-opacity-50 justify-between w-full px-1 pb-[8px]">
                    <div className="flex flex-row items-center gap-2 ">
                      <CalendarDaysIcon className="w-[17px] h-[17px]" />
                      <p className="font-lato font-[500] text-[14.4px]">
                        {activeSlot?.startDateTime
                          ? dayjs(activeSlot.startDateTime).format(
                              "DD MMMM (dddd)"
                            )
                          : "Date TBA"}
                      </p>
                    </div>
                    <div className="flex flex-row items-center justify-center">
                      <ClockIcon className="w-[17px] h-[17px]" />
                      <p className="font-lato text-[14.4px] ml-[2px] uppercase">
                        {dayjs(activeSlot?.startDateTime).format("hh:mm a")}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-row items-center align-middle justify-between w-full mt-[8px]">
                    <div className="w-auto border-none bg-[#9A70261A] bg-op-[10%] rounded-full">
                      <p className="font-lato font-[500] text-[15px] text-[#9A7026] px-2 py-1">
                        {masterclass?.categories[0]?.name || "Spirituality"}
                      </p>
                    </div>
                    <div className="flex flex-row justify-center place-content-evenly items-center">
                      <div className="flex flex-row items-center justify-center">
                        {[
                          ...Array(Math.round(starRating > 0 ? starRating : 4)),
                        ].map(() => {
                          return (
                            <img
                              src="/astro/star-filled.svg"
                              className="h-[14px] w-[14px]"
                            ></img>
                          );
                        })}
                        {[
                          ...Array(
                            5 - Math.round(starRating > 0 ? starRating : 4)
                          ),
                        ].map(() => {
                          return (
                            <img
                              src="/astro/star-empty.svg"
                              className="h-[14px] w-[14px]"
                            ></img>
                          );
                        })}
                      </div>
                      <p className="font-lato text-[16px] ml-[4px]">
                        {starRating > 0 ? starRating : 4} (
                        {formatNumber(reviews)}
                        +)
                      </p>
                    </div>
                  </div>
                </div>{" "}
                <div className="flex flex-row justify-between items-center mt-[8px] w-full px-1">
                  <div className=" flex flex-row items-center justify-center space-x-[7.2px]">
                    <p className="text-[18px] font-lato text-[#9A7026] font-[600]">
                      Free
                    </p>
                    <p className="line-through fiont-lato text-[14px] font-[400]">
                      ₹799
                    </p>
                  </div>
                  <p className="font-lato text-[14.4px]">
                    Offer Ends in{" "}
                    <span className="text-[#9A7026] font-[600]">15:00</span>
                  </p>
                </div>
                <button
                  className="bg-[#0A2133] w-full font-bold px-6 py-4 mt-2 rounded-md text-lg text-white border-none font-lato"
                  onClick={() => {
                    //@ts-ignore
                    document.getElementById("form").scrollIntoView();
                    mixpanel.track("register_now", {
                      masterclass: masterclass?.title,
                      button_location: "top_cta",
                    });
                  }}
                >
                  Register now for free
                </button>
              </section>

              {/* --- Desktop View --- */}
              <section className="max-md:hidden flex flex-col gap-8 mt-[54px]">
                <div className="flex flex-col">
                  <Breadcrumb />
                  <p className="text-[40px] leading-10 font-[500] mt-[16px] font-lato">
                    {masterclass?.title}
                  </p>
                  <p className="text-[20px] text-[#6E7485] font-[300] font-lato mt-[16px]">
                    {masterclass?.description}
                  </p>
                </div>
                <Image
                  width={872}
                  height={491}
                  src={masterclass?.bootcamp?.coverImage || ""}
                  alt="Bootcamp cover image"
                  loading="eager"
                />
              </section>
              <section className="flex flex-col w-full mt-6 gap-4">
                <AccessBar
                  items={accessBarItems}
                  activeItem={accessBarElement}
                  setActiveIcon={setAccessBarElement}
                  masterclassTitle={masterclass?.title}
                />
                {accessBarElement == "overview" && (
                  <div className="flex flex-col text-start">
                    <div
                      className="text-left font-lato prose max-w-none md:hidden"
                      dangerouslySetInnerHTML={{
                        __html: (masterclass?.learnings as string)
                          ?.replaceAll(
                            "<h1>",
                            "<h1 style='color:black;text-align:center;font-family:lato;font-size:20px'>"
                          )
                          ?.replaceAll(
                            "<p>",
                            "<p style='font-size:16px;color:black;letter-spacing:0px;text-align:start;font-family:lato'>"
                          )
                          ?.replaceAll("<li>", "<li style='color:black'>")
                          ?.replaceAll(
                            "<strong>",
                            "<strong style='font-size:20px;font-family:lato;text-align:left'>"
                          )
                          ?.replaceAll(
                            "<h2>",
                            "<h2 style='color:black;text-align:start;font-family:lato;font-size:20px'>"
                          ),
                      }}
                    />
                    <div
                      className="text-left font-lato prose max-w-none md:flex md:flex-col hidden"
                      dangerouslySetInnerHTML={{
                        __html: (masterclass?.learnings as string)
                          ?.replaceAll(
                            "<h1>",
                            "<h1 style='color:black;text-align:center;font-family:lato;font-size:24px;background-color:transparent'>"
                          )
                          ?.replaceAll(
                            "<p>",
                            "<p style='font-size:18px;color:black;letter-spacing:0px;text-align:start;font-family:lato;background-color:transparent'>"
                          )
                          ?.replaceAll(
                            "<li>",
                            "<li style='color:black;background-color:transparent'>"
                          )
                          ?.replaceAll(
                            "<strong>",
                            "<strong style='font-size:24px;font-family:lato;text-align:left;background-color:transparent'>"
                          )
                          ?.replaceAll(
                            "<h2>",
                            "<h2 style='color:black;text-align:start;font-family:lato;font-size:24px;background-color:transparent'>"
                          ),
                      }}
                    />
                    <div className="flex flex-col md:gap-4 justify-center items-stretch gap-6 mt-[32px]">
                      {OverViewCardRawData.map((data: any) => {
                        return (
                          <OverviewCard
                            headerText={data.headerText}
                            headerImagePath={data.headerImagePath}
                            mainBodyText={data.mainBodyText}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                {accessBarElement == "instructor" && (
                  <AboutInstructor
                    teacherName={masterclass?.bootcamp?.teacher?.name}
                    teacherImage={masterclass?.bootcamp?.teacher?.photo}
                    experience={10}
                    studentsTaught={students}
                    reviews={reviews}
                    teacherDescription={masterclass?.bootcamp?.teacher?.bio}
                  />
                )}
                {accessBarElement == "help" && (
                  <ContactUs
                    headerText={`<p className="font-lato text-[20px] font-[500]">
            For More Details About This Course
             </p>`}
                    mainText="If you have any question regarding the course and are not able to find the answer on this page, feel free to contact us"
                    phoneNumber="+919056187997"
                  />
                )}
              </section>
            </div>

            <section id="form" className=" pt-10 w-full md:w-1/3 md:pt-0 ">
              <div className="md:sticky md:top-28 md:right-0">
                <LeadFormMasterclass
                  starRating={starRating > 0 ? starRating : 4}
                  review={reviews}
                  coverImage={masterclass?.bootcamp?.coverImage}
                  masterclassTitle={masterclass?.title}
                  teacherName={masterclass?.bootcamp?.teacher?.name}
                  activeSlotInfo={masterclass?.slots?.find((slot: any) => {
                    return slot.active == true;
                  })}
                  category={masterclass?.bootcamp?.teacher?.category?.name}
                  masterclassId={masterclass?.id}
                />
              </div>
            </section>
            <section className="flex flex-col justify-center items-center pb-[30px] mt-[64px] text-center md:px-[20%] md:hidden">
              <p className="text-2xl font-medium text-black font-lato md:text-[50px]">
                Frequently Asked Questions
              </p>
              {qNAs.map((qna: { que: string; ans: string }, index: number) => {
                return (
                  <div
                    className="w-full flex flex-row justify-center items-center rounded-xl"
                    onClick={() => {
                      setSelectedQna((selectedQna) =>
                        selectedQna == index ? -1 : index
                      );
                    }}
                  >
                    <QuestionAnswerTile
                      FaqQuestion={qna.que}
                      FaqAnswer={qna.ans}
                      className="!px-3 !items-center !justify-between !text-start !text-[16px]"
                      isOpen={selectedQna == index}
                    />
                  </div>
                );
              })}
            </section>
          </div>
        </div>
      </Layout>
    </>
  );
}

export function OverviewCard({
  headerText,
  headerImagePath,
  mainBodyText,
}: {
  headerText: string;
  headerImagePath: string;
  mainBodyText: string;
}) {
  return (
    <div className="flex flex-col px-[29px] py-[16px] max-lg:text-center w-full bg-white bg-opacity-[80%] text-[#00163A] rounded-lg">
      <div className="flex flex-row justify-between items-center">
        <p className="font-lato font-[700] text-[20px]">{headerText}</p>
        <img
          src={headerImagePath}
          alt=""
          className="stroke=[#9A7026] w-[40px] h-[40px]"
        />
      </div>
      <p className="font-lato text-[15px] text-[#5B5B5B] text-left mt-1">
        {mainBodyText}
      </p>
    </div>
  );
}

export async function getServerSideProps(context: any) {
  const { slug } = context.params;
  try {
    const { data } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}masterclasses/masterclass-details/${slug}`,
      {
        headers: {
          "Accept-Encoding": "gzip",
        },
      }
    );
    return {
      props: {
        masterclass: data.data,
      },
    };
  } catch (err) {
    return {
      props: {
        masterclass: {},
      },
    };
  }
}
